import { useEffect, useState } from 'react'

import { Icons } from '@/components/icons'
import {
    Modal as NextUIModal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Link
} from '@/components/nextui'
import { useAuthHook } from '@/hooks'
import { useInscriptionsApi } from '@/services/api/inscriptionsApi'
import { useAuthStore } from '@/store/authStore'
import { MessageLevel } from '@/types'
import { ErrorHandlingStrategy } from '@/types/api'

const modalMotionProps = {
    variants: {
        enter: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.3,
                ease: 'easeOut'
            }
        },
        exit: {
            y: -20,
            opacity: 0,
            transition: {
                duration: 0.2,
                ease: 'easeIn'
            }
        }
    }
}

interface InactiveInscriptionModalProps {
    isOpen: boolean
    setLoadingInscription: (loading: boolean) => void
    onClose?: () => void
}

export const InactiveInscriptionModal = ({
    isOpen,
    setLoadingInscription,
    onClose
}: InactiveInscriptionModalProps): JSX.Element => {
    const { idAspirant, idInscription } = useAuthHook()
    const { updateIdInscription, setInscriptions } = useAuthStore()
    const [inscriptionStatusId, setInscriptionStatusId] = useState<
        number | null
    >(null)
    const inscriptionsApi = useInscriptionsApi({
        messageLevel: MessageLevel.ERRORS,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    useEffect(() => {
        if (!isOpen || !idInscription) {
            return
        }
        void inscriptionsApi
            .getInscription(String(idInscription))
            .then((res) => {
                if (res.status === 200 && res.data) {
                    setInscriptionStatusId(res.data.inscriptionStatusId)
                }
            })
    }, [isOpen, idInscription])

    const handleCreateNewInscription = async (): Promise<void> => {
        if (!idAspirant) {
            return
        }
        setLoadingInscription(true)
        try {
            // La nueva inscripción se crea sin datos académicos: reenviar los de la
            // inscripción inactiva hacía que el backend buscara su oferta (de un periodo
            // ya iniciado) y respondiera 400 sin crear nada. La oferta se elige en el
            // paso académico.
            const created = await inscriptionsApi.createInscription(
                idAspirant,
                {
                    academicAdmission: null,
                    idAcademicOffering: null,
                    isClaseOyenteValid: 0,
                    isStatusRejectedValid: 1
                }
            )

            if (created.status !== 200 && created.status !== 201) {
                setLoadingInscription(false)

                return
            }

            const listRes = await inscriptionsApi.getInscriptions(idAspirant)

            if (listRes.status === 200 && Array.isArray(listRes.data)) {
                setInscriptions(
                    listRes.data.map((item) => ({
                        id: item.id,
                        isActive: item.isActive
                    }))
                )

                const activeId =
                    listRes.data.find((item) => item.isActive)?.id ?? null

                updateIdInscription(activeId)
            } else {
                updateIdInscription(null)
            }
        } catch {
            setLoadingInscription(false)
        }
    }

    return (
        <NextUIModal
            isOpen={isOpen}
            placement="center"
            backdrop="opaque"
            hideCloseButton
            isDismissable={false}
            isKeyboardDismissDisabled
            motionProps={modalMotionProps}
        >
            <ModalContent>
                <ModalHeader className="flex flex-col gap-1 text-primary text-xl font-bold items-center">
                    <Icons.ExclamationTriangle /> Inscripción Inactiva
                </ModalHeader>
                {inscriptionStatusId === 4 ? (
                    <>
                        <ModalBody>
                            <p>
                                La inscripción actual se encuentra inactiva.
                                Para continuar con tu proceso debes solicitar la
                                reactivación de tu cupo con el area de
                                admisiones en la mesa de servicios.
                            </p>
                            <p className="text-sm">
                                <Link
                                    isExternal
                                    showAnchorIcon
                                    href="https://universidadean.edu.co/mesa-de-servicios"
                                    className="text-primary"
                                >
                                    Mesa de Servicio
                                </Link>
                            </p>
                        </ModalBody>
                        <ModalFooter className="flex justify-center">
                            <Button color="primary" onPress={onClose}>
                                Cerrar
                            </Button>
                        </ModalFooter>
                    </>
                ) : inscriptionStatusId === 3 ? (
                    <>
                        <ModalBody>
                            <p>
                                La inscripción actual ha finalizado. Si deseas
                                inscribirte en un nuevo programa o período
                                académico, puedes hacerlo ahora. Si no deseas
                                continuar, haz clic en el botón Cerrar.
                            </p>
                        </ModalBody>
                        <ModalFooter className="flex justify-center">
                            <Button color="primary" onPress={onClose}>
                                Cerrar
                            </Button>
                            <Button
                                color="primary"
                                onPress={() =>
                                    void handleCreateNewInscription()
                                }
                            >
                                Crear nueva inscripción
                            </Button>
                        </ModalFooter>
                    </>
                ) : (
                    <>
                        <ModalBody>
                            <p>
                                La inscripción actual se encuentra inactiva.
                                Para continuar con tu proceso para una nueva
                                fecha academica, necesitas crear una nueva
                                inscripción.
                            </p>
                        </ModalBody>
                        <ModalFooter className="flex justify-center">
                            <Button
                                color="primary"
                                onPress={() =>
                                    void handleCreateNewInscription()
                                }
                            >
                                Crear nueva inscripción
                            </Button>
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </NextUIModal>
    )
}
